import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { setDialogOpen } from '../../store/Actions';
import { buildDetailPath, useAppNavigate } from '../../utils/Routing';
import { MyAvatar } from '../common/MyAvatar';
import '../entityView/EntityView.scss';

const FriendCard = ({friend, eventCount, onOpen, delay}) => {
  return (
    <button
      type="button"
      className="entity-view__card entity-view__card--friend"
      style={{'--card-delay': `${delay}ms`, '--card-accent': '#f59e0b'}}
      onClick={onOpen}
    >
      <div className="entity-view__friend-row">
        <MyAvatar
          userAccount={friend}
          style={{width:48, height:48, fontSize:18, flexShrink:0}}
        />
        <div className="entity-view__friend-row-text">
          <div className="entity-view__friend-name">{friend.name}</div>
          <div className="entity-view__friend-meta">
            {eventCount > 0
              ? `${eventCount} ${eventCount === 1 ? 'event' : 'events'}`
              : 'No events yet'}
          </div>
        </div>
      </div>
    </button>
  );
};

export function FriendsView() {
  const user = useSelector(state => state.loggedInUser);
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const friendList = useMemo(() => Object.values(friends), [friends]);

  // Compute per-friend event counts so each card can show a meaningful stat.
  const eventCountByFriend = useMemo(() => {
    const counts = {};
    Object.values(events).forEach(event => {
      (event.friendIds || []).forEach(id => {
        counts[id] = (counts[id] || 0) + 1;
      });
    });
    return counts;
  }, [events]);

  // Sort by event count (desc), then by name (asc) for a useful default order.
  const sortedFriends = useMemo(() => {
    return [...friendList].sort((a, b) => {
      const diff = (eventCountByFriend[b.id] || 0) - (eventCountByFriend[a.id] || 0);
      if(diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [friendList, eventCountByFriend]);

  const totalTaggings = sortedFriends.reduce(
    (sum, friend) => sum + (eventCountByFriend[friend.id] || 0),
    0
  );

  const ownerName = viewingUserAccount?.displayName || viewingUserAccount?.name || 'User';
  const heroTitle = isOwnMap ? 'My Friends' : `${ownerName}'s Friends`;
  const heroSubtitle = friendList.length > 0
    ? `${friendList.length} ${friendList.length === 1 ? 'person' : 'people'} in your story`
    : 'Add the people in your life';

  const username = viewingUserAccount?.username;
  const openFriend = (friend) => {
    if(!username) return;
    navigateTo(buildDetailPath(username, 'Friends', friend.id));
  };

  const openAddFriendDialog = () => {
    dispatch(setDialogOpen('Edit Friend', {
      parentDialog: '',
      dialogTitle: 'Add Friend',
      friend: {
        userId: user?.uid,
        name: '',
      },
    }));
  };

  return (
    <main className="entity-view">
      <section className="entity-view__hero">
        <div className="entity-view__hero-glow entity-view__hero-glow--friends" aria-hidden="true"/>
        <div className="entity-view__hero-content">
          <div className="entity-view__hero-icon" aria-hidden="true">👥</div>
          <h1 className="entity-view__hero-title">{heroTitle}</h1>
          <div className="entity-view__hero-tagline">People</div>
          <div className="entity-view__hero-subtitle">{heroSubtitle}</div>
          {isOwnMap &&
            <div className="entity-view__hero-actions">
              <button
                type="button"
                className="entity-view__hero-add-btn"
                onClick={openAddFriendDialog}
              >
                <AddIcon/>
                <span>Add Friend</span>
              </button>
            </div>
          }
        </div>
      </section>

      {friendList.length > 0 &&
        <section
          className="entity-view__stats-strip"
          aria-label="Friend stats"
        >
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '120ms', '--stat-accent': '#f59e0b'}}
          >
            <div className="entity-view__stat-value">{friendList.length}</div>
            <div className="entity-view__stat-label">Friends</div>
          </div>
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '200ms', '--stat-accent': '#ec4899'}}
          >
            <div className="entity-view__stat-value">{totalTaggings}</div>
            <div className="entity-view__stat-label">Taggings</div>
          </div>
          {sortedFriends[0] &&
            <div
              className="entity-view__stat"
              style={{'--stat-delay': '280ms', '--stat-accent': '#6366f1'}}
            >
              <div className="entity-view__stat-value" style={{fontSize:18, lineHeight:1.2}}>
                {sortedFriends[0].name}
              </div>
              <div className="entity-view__stat-label">Most Tagged</div>
            </div>
          }
        </section>
      }

      {friendList.length > 0 &&
        <section className="entity-view__panel">
          <header className="entity-view__panel-header">
            <h2>All Friends</h2>
            <span className="entity-view__panel-header-count">
              {friendList.length} total
            </span>
          </header>
          <div className="entity-view__grid">
            {sortedFriends.map((friend, idx) => (
              <FriendCard
                key={friend.id}
                friend={friend}
                eventCount={eventCountByFriend[friend.id] || 0}
                onOpen={() => openFriend(friend)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {friendList.length === 0 &&
        <section className="entity-view__empty">
          <div className="entity-view__empty-icon" aria-hidden="true">👋</div>
          <div className="entity-view__empty-title">No friends yet</div>
          <div className="entity-view__empty-text">
            Add the people in your life so you can tag them in your memories.
          </div>
          {isOwnMap &&
            <button
              type="button"
              className="entity-view__hero-add-btn"
              onClick={openAddFriendDialog}
            >
              <AddIcon/>
              <span>Add your first friend</span>
            </button>
          }
        </section>
      }
    </main>
  );
}
